import React, { useEffect, useMemo, useState } from 'react';
import { useWorld } from '../../contexts/WorldDataContext';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { NpcShopCard } from '../../components/world/NpcShopCard';
import { useHashScroll } from '../../hooks/useHashScroll';
import { npcs } from '../../data/guide/npcs';
import { npcShopPendings } from '../../data/guide/shops';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { compareMapGroups, locationPoints, serviceLabel, UNLOCATED } from '../../utils/npcShops';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { EntityThumb } from '../../components/world/EntityThumb';
import { npcPhotos } from '../../data/guide/npcPhotoSheets';
import { npcDisplayName, npcIdentities } from '../../data/guide/npcIdentity';
import { monsterPath } from '../../utils/worldFormat';
import type { NpcServiceKind } from '../../types/shop';
const FILTERS: (NpcServiceKind | 'todos')[] = ['todos', 'venta', 'reparacion', 'misiones', 'combinaciones', 'acceso'];

/** "NPC y tiendas": NPC por mapa (Lorencia y Noria primero), con ubicación, servicios e inventario. */
export function NpcShopsGuide() {
  const world = useWorld();
  const [service, setService] = useState<NpcServiceKind | 'todos'>('todos');
  useHashScroll();
  useEffect(() => {
    document.title = 'NPC y tiendas · Guía MU Vanguard';
  }, []);
  const groups = useMemo(() => {
    const snap = {
      status: world.status,
      data: world.data,
      error: world.error
    };
    const map = new Map<string, {
      npc: (typeof npcs)[number];
      loc: ReturnType<typeof resolveNpcLocation>;
    }[]>();
    npcs.forEach(npc => {
      if (service !== 'todos' && !npc.services?.some(s => s.kind === service)) return;
      const loc = resolveNpcLocation(npc, snap);
      const names = Array.from(new Set(locationPoints(loc).map(p => p.mapName)));
      // Un NPC que se desplaza entre varios mapas se muestra una sola vez, en el primero.
      const key = names[0] ?? UNLOCATED;
      map.set(key, [...(map.get(key) ?? []), {
        npc,
        loc
      }]);
    });
    return Array.from(map.entries()).sort(([a], [b]) => compareMapGroups(a, b));
  }, [world.status, world.data, world.error, service]);

  // NPC del paquete de fotos que aún no tienen ficha propia en la guía (solo si existen en el catálogo).
  const otherNpcs = useMemo(() => {
    const w = world.data;
    if (!w || service !== 'todos') return [];
    const guideIds = new Set(npcs.map(n => n.monsterId));
    // Paquete de fotos + NPC identificados por el staff; sin foto también se listan. Los unificados no se duplican.
    const ids = new Set([...npcPhotos.map(p => p.id), ...npcIdentities.filter(n => n.mergedInto === undefined).map(n => n.id)]);
    return Array.from(ids).filter(id => !guideIds.has(id)).map(id => w.monsters.get(id)).filter((m): m is NonNullable<typeof m> => !!m).map(m => ({
      id: m.id,
      name: npcDisplayName(m.id, m.name)
    })).sort((a, b) => a.name.localeCompare(b.name, 'es') || a.id - b.id);
  }, [world.data, service]);
  return <div className="mx-auto w-full max-w-5xl" data-id="element-2452" data-mp="1imwsx2z93z5r">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas'
    }]} eyebrow="Ciudades" title="NPC y tiendas" data-id="element-2453" data-mp="1io7xh50itaxx">
        <p className="mt-3 max-w-2xl text-sm md:text-base text-gray-300" data-id="element-2454" data-mp="1rn36eouy8z4x">
          Dónde está cada NPC, qué servicio presta y qué vende. Las funciones marcadas como clásicas son de MU Online Season 6 y están pendientes de validar en Vanguard.
        </p>
      </WorldPageHeader>

      <div role="group" aria-label="Filtrar por servicio" className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0" data-id="element-2455" data-mp="21jg7i7stqf9p">
        {FILTERS.map(f => <button key={f} type="button" aria-pressed={service === f} onClick={() => setService(f)} className={`h-9 shrink-0 rounded-full border px-3.5 text-sm font-semibold transition-colors duration-150 ${service === f ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-white' : 'border-white/10 text-gray-300 hover:text-white'}`} data-id="element-2456" data-mp="2aecovnmp6y8n">
            {f === 'todos' ? 'Todos' : serviceLabel[f]}
          </button>)}
      </div>

      <div className="space-y-10" data-id="element-2457" data-mp="rfeqyylahsgo">
        {groups.length === 0 && <p className="text-gray-400" data-id="element-2458" data-mp="weiylkskfi4w">Ningún NPC presta este servicio según los datos disponibles.</p>}
        {groups.map(([mapName, list]) => <WorldSection key={mapName} id={`mapa-${mapName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} title={mapName} count={list.length} data-id="element-2459" data-mp="3uxg6ltx007he">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-2460" data-mp="8oomx6jzfzm8">
              {list.map(({
            npc,
            loc
          }) => <NpcShopCard key={npc.id} npc={npc} loc={loc} w={world.data} data-id="element-2461" data-mp="dni3j7ukg6qm" />)}
            </div>
          </WorldSection>)}

        {otherNpcs.length > 0 && <WorldSection id="otros-npc" title="Otros NPC identificados" count={otherNpcs.length} data-id="element-2462" data-mp="1k3amhzz4hebv">
            <p className="mb-4 max-w-2xl text-sm text-gray-400" data-id="element-2463" data-mp="1p13tuwc5wedq">Identificados por su ID. Foto, función, ubicación y qué venden se documentan por separado; lo que falta queda pendiente.</p>
            <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2464" data-mp="34mxp9vudds2v">
              {otherNpcs.map(m => <li key={m.id} className="flex items-center gap-3 rounded-2xl border border-white/10 p-3" data-id="element-2465" data-mp="2zt1sgwpueb9a">
                  <EntityThumb entity={{
              type: 'monster',
              id: m.id
            }} name={m.name} size="card" fallback={<UserRound className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2467" data-mp="2py9qzht53498" />} data-id="element-2466" data-mp="2utp6ee6sp87r" />
                  <span className="min-w-0" data-id="element-2468" data-mp="2lubr34vzppf4">
                    <Link to={monsterPath(m.id)} className="block truncate font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2469" data-mp="2gw0n3asdm94t">{m.name}</Link>
                    <span className="block text-xs text-gray-500" data-id="element-2470" data-mp="2bza68l8qz9ic">Función no documentada · ID {m.id}</span>
                  </span>
                </li>)}
            </ul>
          </WorldSection>}

        <WorldSection id="datos-que-faltan" title="Datos que faltan" count={npcShopPendings.length} data-id="element-2471" data-mp="24m2ibr52ktbc">
          <ol className="space-y-3" data-id="element-2472" data-mp="29c98vwj56nw8">
            {npcShopPendings.map((p, i) => <li key={p.what} className="flex gap-3 border-t border-white/10 pt-3 first:border-0 first:pt-0" data-id="element-2473" data-mp="1unmtjutilw50" data-mpi={i}>
                <span className="font-mono text-sm text-gray-500" data-id="element-2474" data-mp="1zmg53hp8rd2j">{i + 1}</span>
                <span data-id="element-2475" data-mp="1zkda0j98etqy">
                  <span className="block font-semibold text-white" data-id="element-2476" data-mp="2nbr0skrqih9t">{p.what}</span>
                  <span className="block text-sm text-gray-400" data-id="element-2477" data-mp="2sbdaik8ctfdv">{p.detail}</span>
                </span>
              </li>)}
          </ol>
        </WorldSection>
      </div>
    </div>;
}